/**
 * Contrato de plugin do Family Life OS.
 *
 * Regra de arquitetura (ADR-0002): TODA a funcionalidade de negocio e um plugin.
 * O nucleo nao conhece "financas" nem "saude" — conhece plugins.
 *
 * Um plugin declara o que faz num manifesto estatico (legivel sem o carregar) e
 * recebe tudo aquilo de que precisa por injecao, atraves do PluginContext.
 * Um plugin NUNCA importa outro plugin diretamente: fala com ele por eventos
 * ou por servicos publicados no registro.
 */
import type { AuditActor, EntityDescriptor } from './records';
import type { FamilyRole, Scope } from './permissions';
import type { IntentDescriptor } from './ai';
import type { Validator } from './validation';
import type { ComponentType } from 'react';

/** Uma rota que o plugin acrescenta a navegacao. */
export interface PluginRoute {
  /** Caminho relativo ao plugin, ex. '/' ou '/transacao/:id'. */
  path: string;
  /** Componente React. Carregado por `lazy` para nao pesar no arranque. */
  component: () => Promise<{ default: ComponentType<unknown> }>;
  /** Titulo mostrado na barra superior. */
  title: string;
  /** Escopo necessario para abrir. Sem ele, a rota nem aparece. */
  requires?: Scope;
  /** Se true, aparece no menu principal. */
  primary?: boolean;
}

/** Cartao que o plugin publica na tela inicial. */
export interface WidgetDescriptor {
  id: string;
  title: string;
  /** Ordem sugerida. Menor aparece primeiro. O usuário pode reordenar. */
  order: number;
  requires?: Scope;
  component: () => Promise<{ default: ComponentType<unknown> }>;
}

/** Regra de automacao que o plugin traz de origem (Milestone 10). */
export interface AutomationDescriptor {
  id: string;
  /** Descricao em portugues, ex. 'Quando um medicamento atrasa, avisar a filha'. */
  description: string;
  /** Evento que dispara a regra. */
  trigger: string;
  /** Se false, o usuário tem de a ligar explicitamente. */
  enabledByDefault: boolean;
}

export interface PluginManifest {
  /** Identificador estavel e minusculo, ex. 'finance'. Prefixa entidades e escopos. */
  id: string;
  /** Nome em portugues mostrado ao usuário, ex. 'Financas'. */
  name: string;
  /** Semver. */
  version: string;
  /** Uma frase, em portugues, para a tela de configuracoes. */
  description: string;
  /** Emoji ou nome de icone. Elder-friendly: preferimos emoji, sao reconheciveis. */
  icon: string;
  /** Escopos que este plugin define e pode pedir. */
  permissions: Scope[];
  /** Outros plugins que tem de estar ativos antes deste. Grafo aciclico. */
  dependencies: string[];
  /** Entidades de dados que este plugin possui. Ninguem mais escreve nelas. */
  entities: EntityDescriptor[];
  routes: PluginRoute[];
  widgets?: WidgetDescriptor[];
  /** Intencoes de voz/texto que este plugin sabe tratar. */
  intents?: IntentDescriptor[];
  automations?: AutomationDescriptor[];
  /** Marca plugins ainda por implementar (Milestones 6-11). */
  status?: 'stable' | 'beta' | 'scaffold';
}

/**
 * Tudo o que um plugin pode usar. Injetado no `register`.
 * Deliberadamente estreito: se algo nao esta aqui, o plugin nao lhe deve tocar.
 */
export interface PluginContext {
  manifest: PluginManifest;
  logger: Logger;
  events: EventBusLike;
  /** Fabrica de repositorios tipados sobre o IndexedDB local. */
  repository: <T>(entity: string) => RepositoryLike<T>;
  /**
   * Onde registrar o `Validator` de cada entidade que o plugin possui.
   * A regra 4 torna a validacao obrigatoria, logo tem de haver um lugar para a
   * declarar sem recorrer a casts.
   */
  validators: ValidatorRegistryLike;
  /** Publica um servico para outros plugins consumirem por id. */
  provide: <T>(serviceId: string, impl: T) => void;
  /** Consome um servico publicado por outro plugin. `null` se ausente. */
  consume: <T>(serviceId: string) => T | null;
  /** Verifica permissao do usuário ativo. */
  can: (scope: Scope) => boolean;
  /** Configuracoes persistidas com escopo neste plugin. */
  settings: SettingsLike;
  /** Agenda notificacoes locais e por familia. */
  notify: NotifierLike;
  /** Sessao atual: quem esta a usar e em que familia. */
  session: () => SessionInfo;
}

export interface SessionInfo {
  memberId: string;
  familyId: string;
  displayName: string;
  role: FamilyRole;
}

/** Ciclo de vida de um plugin. Todos os passos podem ser assincronos. */
export interface FlosPlugin {
  manifest: PluginManifest;
  /**
   * Chamado uma vez no arranque, depois das dependencias.
   * Deve registrar servicos e ouvintes. NAO deve fazer trabalho pesado nem I/O de rede.
   */
  register(ctx: PluginContext): void | Promise<void>;
  /** Chamado quando o usuário abre o plugin pela primeira vez na sessao. */
  activate?(ctx: PluginContext): void | Promise<void>;
  /** Chamado ao desativar o plugin nas configuracoes. Deve libertar recursos. */
  deactivate?(ctx: PluginContext): void | Promise<void>;
  /** Migracoes de dados locais, por versao do plugin. */
  migrations?: Record<string, (ctx: PluginContext) => Promise<void>>;
}

// --- Interfaces estruturais, para evitar dependencia circular com @flos/core ---

export interface Logger {
  debug(msg: string, meta?: Record<string, unknown>): void;
  info(msg: string, meta?: Record<string, unknown>): void;
  warn(msg: string, meta?: Record<string, unknown>): void;
  error(msg: string, meta?: Record<string, unknown>): void;
  child(bindings: Record<string, unknown>): Logger;
}

export interface EventBusLike {
  emit(event: string, payload?: unknown): void;
  on(event: string, handler: (payload: unknown) => void): () => void;
}

/**
 * Opcoes de leitura. Separadas do filtro de campos para que `limit`, `orderBy` e
 * afins nunca colidam com um campo real de um registro.
 */
export interface ListOptions {
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: string;
  direction?: 'asc' | 'desc';
}

/** Proveniencia de uma escrita. Sem isto, uma escrita da IA nao e atribuivel. */
export interface WriteOptions {
  actor?: AuditActor;
  /** Liga esta escrita a interpretacao da IA que a originou. */
  aiTraceId?: string;
  automationRuleId?: string;
  reason?: string;
}

export interface RepositoryLike<T> {
  get(id: string): Promise<T | null>;
  list(filter?: Record<string, unknown>, options?: ListOptions): Promise<T[]>;
  count(filter?: Record<string, unknown>, options?: ListOptions): Promise<number>;
  create(input: Partial<T>, options?: WriteOptions): Promise<T>;
  update(id: string, patch: Partial<T>, options?: WriteOptions): Promise<T>;
  remove(id: string, options?: WriteOptions): Promise<void>;
  restore(id: string, options?: WriteOptions): Promise<T>;
  /** Repoe o registro no estado anterior a uma entrada de auditoria (ADR-0004). */
  revert(auditEntryId: string, options?: WriteOptions): Promise<T>;
}

/** Onde os plugins registam a validacao das suas entidades (regra 4). */
export interface ValidatorRegistryLike {
  register(entity: string, validator: Validator<unknown>): void;
  get(entity: string): Validator<unknown> | undefined;
}

export interface SettingsLike {
  get<T>(key: string, fallback: T): Promise<T>;
  set<T>(key: string, value: T): Promise<void>;
}

export interface NotifierLike {
  local(input: { title: string; body: string; at?: Date; tag?: string }): Promise<void>;
  family(input: { title: string; body: string; toRoles?: string[] }): Promise<void>;
}

/** Valida um manifesto antes de o registrar. Devolve a lista de problemas. */
export function validateManifest(m: PluginManifest): string[] {
  const problems: string[] = [];
  if (!/^[a-z][a-z0-9-]*$/.test(m.id)) {
    problems.push(`id "${m.id}" deve ser minusculo, comecar por letra e usar so - como separador`);
  }
  if (!/^\d+\.\d+\.\d+/.test(m.version)) {
    problems.push(`version "${m.version}" nao e semver`);
  }
  if (!m.name.trim()) problems.push('name nao pode ser vazio');
  for (const entity of m.entities) {
    if (!entity.name.startsWith(`${m.id}.`)) {
      problems.push(
        `entidade "${entity.name}" tem de comecar por "${m.id}." — cada plugin so possui o seu namespace`,
      );
    }
  }
  for (const scope of m.permissions) {
    const plugin = scope.split(':')[0];
    if (plugin !== m.id && plugin !== '*') {
      problems.push(`escopo "${scope}" nao pertence ao plugin "${m.id}"`);
    }
  }
  if (m.dependencies.includes(m.id)) {
    problems.push('um plugin nao pode depender de si mesmo');
  }
  return problems;
}
